export const WORK_HEALTH_PLATFORMS = ['apollo', 'anthropic', 'higgsfield', 'email', 'generic'] as const;
export type WorkHealthPlatform = (typeof WORK_HEALTH_PLATFORMS)[number];

export const WORK_HEALTH_PLATFORM_META: Record<
  WorkHealthPlatform,
  { label: string; unit: string; sourceKind: 'auto' | 'manual' }
> = {
  apollo: { label: 'Apollo', unit: 'actions', sourceKind: 'auto' },
  // anthropic auto-sync is deferred (org-total-only) and email sends are
  // manual-only in v1 — only apollo activity mirrors from gtm_activities.
  anthropic: { label: 'AI drafting', unit: 'drafts', sourceKind: 'manual' },
  higgsfield: { label: 'Higgsfield', unit: 'renders', sourceKind: 'manual' },
  email: { label: 'Email marketing', unit: 'sends', sourceKind: 'manual' },
  generic: { label: 'Other', unit: 'actions', sourceKind: 'manual' },
};

export const WORK_HEALTH_WINDOWS = ['daily', 'weekly', 'biweekly', 'monthly'] as const;
export type WorkHealthWindow = (typeof WORK_HEALTH_WINDOWS)[number];
export const WORK_HEALTH_WINDOW_DAYS: Record<WorkHealthWindow, number> = {
  daily: 1,
  weekly: 7,
  biweekly: 14,
  monthly: 30,
};

export type WorkHealthStatus = 'active' | 'low' | 'neglected';
export type WorkHealthUsageSource = 'gtm_auto' | 'manual';

// What produced a usage row: the gtm activity type on auto-sync, 'manual' on
// manual entry. A responsibility may scope itself to one of these (null = any).
export const WORK_HEALTH_SOURCE_TYPES = ['import', 'enrich', 'manual'] as const;
export type WorkHealthSourceType = (typeof WORK_HEALTH_SOURCE_TYPES)[number];

export interface WorkHealthMember {
  id: string;
  name: string;
  email: string | null;
  linkedUserId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkHealthResponsibility {
  id: string;
  memberId: string;
  area: string;
  platform: WorkHealthPlatform;
  // Named `cadence` (DB column too) — `window` is a reserved keyword in Postgres.
  cadence: WorkHealthWindow;
  minCount: number;
  // null = any source_type on the platform counts; set = only that type.
  sourceType: WorkHealthSourceType | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkHealthUsage {
  id: string;
  memberId: string;
  platform: WorkHealthPlatform;
  occurredAt: string;
  count: number;
  amount: number | null;
  unit: string | null;
  source: WorkHealthUsageSource;
  sourceType: WorkHealthSourceType | null;
  extRef: string | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface WorkHealthResponsibilityReport {
  responsibility: WorkHealthResponsibility;
  status: WorkHealthStatus;
  windowCount: number;
  windowBurn: number;
  lastActivityAt: string | null;
}

export interface WorkHealthMemberReport {
  member: WorkHealthMember;
  responsibilities: WorkHealthResponsibilityReport[];
  neglectedCount: number;
}

export interface WorkHealthReport {
  members: WorkHealthMemberReport[];
  generatedAt: string;
}
